import React, {useState, useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {useCart} from "../../hooks/useCart";
import {useAuth} from "../../hooks/useAuth";
import {DatafastService} from "../../../core/services/DatafastService";
import {CheckoutService} from "../../../core/services/CheckoutService";
import {CheckoutItemsService} from "../../../infrastructure/services/CheckoutItemsService";
import type {DatafastCheckoutRequest} from "../../../core/services/DatafastService";
import type {PaymentMethod, PaymentInfo} from "../../../core/services/CheckoutService";
import {NotificationType} from "../../contexts/CartContext";
import {useDatafastCSP} from "../../hooks/useDatafastCSP";
import type {CheckoutData} from "../../../types/checkout";
import {
	validateDatafastCardFields,
	focusFirstMissingDatafastField,
	showInlineDatafastError,
	clearInlineDatafastError,
	INSTALLMENT_OPTIONS,
	CREDIT_TYPE_OPTIONS,
} from "../../../utils/datafastCardValidation";
import type { DatafastMissingField } from "../../../utils/datafastCardValidation";
import { ensureDatafastWidgetSelectors, ensureDatafastSaveCardOption } from "../../../utils/datafastWidgetUi";

type DatafastServiceInstance = InstanceType<typeof DatafastService>;
type DatafastSavedCard = Awaited<
	ReturnType<DatafastServiceInstance["getSavedCards"]>
>[number];

declare global {
	interface Window {
		jQuery?: unknown;
		$?: unknown;
		__bcommerceJQueryLoader?: Promise<void>;
	}
}

const ensureGlobalJQuery = (): Promise<void> => {
	if (typeof window === "undefined") {
		return Promise.resolve();
	}

	if (window.jQuery && window.$) {
		return Promise.resolve();
	}

	if (window.__bcommerceJQueryLoader) {
		return window.__bcommerceJQueryLoader;
	}

	window.__bcommerceJQueryLoader = new Promise<void>((resolve, reject) => {
		const existing = document.querySelector<HTMLScriptElement>('script[data-bcommerce-jquery="true"]');
		if (existing) {
			if (existing.dataset.loaded === "true") {
				resolve();
				return;
			}

			const handleLoad = () => {
				existing.dataset.loaded = "true";
				resolve();
			};
			const handleError = (event: Event) => {
				existing.removeEventListener("load", handleLoad);
				existing.removeEventListener("error", handleError);
				reject(event);
			};

			existing.addEventListener("load", handleLoad, { once: true });
			existing.addEventListener("error", handleError, { once: true });
			return;
		}

		const script = document.createElement("script");
		script.src = "https://code.jquery.com/jquery-3.6.0.min.js";
		script.async = true;
		script.crossOrigin = "anonymous";
		script.dataset.bcommerceJquery = "true";

		script.onload = () => {
			script.dataset.loaded = "true";
			resolve();
		};

		script.onerror = (event) => {
			console.warn("⚠️ No fue posible cargar jQuery para el widget de Datafast.", event);
			reject(event instanceof Error ? event : new Error("No se pudo cargar jQuery"));
		};

		document.head.appendChild(script);
	});

	return window.__bcommerceJQueryLoader;
};

const CONTROLLED_WIDGET_FIELDS = {
	createRegistration: "createRegistration",
	recurring: "recurring.numberOfInstallments",
	creditType: "customParameters[SHOPPER_TIPOCREDITO]",
} as const;

const dedupeRestrictedDatafastFields = () => {
	const form = document.querySelector("form.paymentWidgets");
	if (!form) {
		return;
	}

	const escapeNameForSelector = (name: string) =>
		name.replace(/\[/g, "\\[").replace(/\]/g, "\\]");

	const enforceSingleField = (name: string) => {
		const selector = `[name="${escapeNameForSelector(name)}"]`;
		const elements = Array.from(
			form.querySelectorAll<HTMLInputElement | HTMLSelectElement>(selector)
		);

		if (elements.length <= 1) {
			return;
		}

		const primary =
			elements.find((element) => element.dataset?.bcommerceManaged === "true") ??
			elements.find((element) => typeof element.dataset?.bcommerceWidget === "string") ??
			elements[0];

		elements.forEach((element) => {
			if (element === primary) {
				element.dataset.bcommerceManaged = "true";
				if ("disabled" in element) {
					(element as HTMLInputElement | HTMLSelectElement).disabled = false;
				}
				return;
			}

			if (element.hasAttribute("name")) {
				element.removeAttribute("name");
				element.setAttribute("data-bcommerce-removed-name", name);
			}
			if ("disabled" in element) {
				(element as HTMLInputElement | HTMLSelectElement).disabled = true;
			}
		});
	};

	enforceSingleField(CONTROLLED_WIDGET_FIELDS.createRegistration);
	enforceSingleField(CONTROLLED_WIDGET_FIELDS.recurring);
	enforceSingleField(CONTROLLED_WIDGET_FIELDS.creditType);
};

let dedupeFrame: number | null = null;
const scheduleRestrictedFieldDedupe = () => {
	if (typeof window === "undefined") {
		return;
	}

	if (dedupeFrame !== null) {
		cancelAnimationFrame(dedupeFrame);
	}

	dedupeFrame = requestAnimationFrame(() => {
		dedupeFrame = null;
		dedupeRestrictedDatafastFields();
	});
};
const insertCertificationBadge = () => {
	if (typeof document === "undefined") {
		return;
	}

	const forms = document.querySelectorAll<HTMLFormElement>(
		"form.wpwl-form-card, form.wpwl-form-registrations"
	);

	forms.forEach((form) => {
		const button = form.querySelector(".wpwl-button");
		if (!button || !button.parentElement) {
			return;
		}

		if (form.querySelector('[data-bcommerce-cert-badge=\"true\"]')) {
			return;
		}

		const badge = document.createElement("div");
		badge.setAttribute("data-bcommerce-cert-badge", "true");
		badge.innerHTML =
			'<br/><br/><img src="https://www.datafast.com.ec/images/verified.png" style="display:block;margin:15px auto; width:100%;">';

		button.parentElement.insertBefore(badge, button);
	});
};
interface DatafastPaymentButtonProps {
	onSuccess?: (orderData: any) => void;
	onError?: (error: string) => void;
	checkoutData?: CheckoutData; // ✅ NUEVO: Objeto temporal con datos validados
	// ✅ LEGACY: Mantener soporte para shippingAddress por compatibilidad
	shippingAddress?: {
		name?: string;
		street?: string;
		city?: string;
		state?: string;
		postalCode?: string;
		country?: string;
		phone?: string;
		identification?: string;
	};
}

interface FormData {
	address: string;
	city: string;
	country: string;
	given_name: string;
	middle_name: string;
	surname: string;
	phone: string;
	doc_id: string;
}

const DatafastPaymentButton: React.FC<DatafastPaymentButtonProps> = ({
	onSuccess,
	onError,
	checkoutData,
}) => {
	// 🔓 Desactivar CSP temporalmente para permitir scripts de Datafast
	useDatafastCSP();
	
	// ✅ MAPEO DE PAÍSES: nombres completos a códigos ISO 3166-1 alpha-2
	const countryMapping: Record<string, string> = {
		"Ecuador": "EC",
		"Colombia": "CO", 
		"Perú": "PE",
		"Peru": "PE",
		"Estados Unidos": "US",
		"United States": "US",
		"USA": "US",
		// Si ya viene como código, lo dejamos igual
		"EC": "EC",
		"CO": "CO", 
		"PE": "PE",
		"US": "US"
	};
	
	const navigate = useNavigate();
	const {cart, clearCart, showNotification, appliedDiscount} = useCart();
	const {user} = useAuth();
	const [isLoading, setIsLoading] = useState(false);
	// ✅ FUNCIÓN HELPER: Mapear país a código ISO (debe estar antes del useState)
	const mapCountryCode = (country: string): string => {
		return countryMapping[country] || country.substring(0, 2).toUpperCase();
	};

	// ✅ FUNCIÓN HELPER: Extraer cédula (10 dígitos) desde RUC (13 dígitos)
	const extractCedulaFromRUC = (identification: string): { cedula: string; wasRuc: boolean; message?: string } => {
		const cleanId = identification.replace(/\D/g, ''); // Solo números

		if (cleanId.length === 10) {
			// Ya es una cédula válida
			if (!/^\d{10}$/.test(cleanId)) {
				return { cedula: cleanId, wasRuc: false, message: "Cédula debe tener 10 dígitos numéricos" };
			}
			return { cedula: cleanId, wasRuc: false };
		} else if (cleanId.length === 13) {
			// Es RUC, extraer cédula (primeros 10 dígitos)
			const extractedCedula = cleanId.substring(0, 10);
			return {
				cedula: extractedCedula,
				wasRuc: true,
				message: `Se extrajo automáticamente la cédula ${extractedCedula} del RUC ${cleanId}`
			};
		} else {
			// Longitud inválida
			return {
				cedula: cleanId,
				wasRuc: false,
				message: `Identificación inválida: debe tener 10 dígitos (cédula) o 13 dígitos (RUC)`
			};
		}
	};

const [showWidget, setShowWidget] = useState(false);
const [checkoutResponse, setCheckoutResponse] = useState<any>(null);
const [showForm, setShowForm] = useState(false);
const [widgetLoaded, setWidgetLoaded] = useState(false);
const [calculatedTotals, setCalculatedTotals] = useState<any>(null);
const [savedCards, setSavedCards] = useState<DatafastSavedCard[]>([]);
const [savedCardsLoading, setSavedCardsLoading] = useState<boolean>(true);
const [paymentMode, setPaymentMode] = useState<"new" | "saved">("new");
const [selectedSavedCardId, setSelectedSavedCardId] = useState<number | null>(null);
const [installments, setInstallments] = useState<number>(INSTALLMENT_OPTIONS[0] ?? 0);
const [creditType, setCreditType] = useState<string>(CREDIT_TYPE_OPTIONS[0]?.value ?? "00");
const [saveCard, setSaveCard] = useState<boolean>(() => {
	try {
		return localStorage.getItem("datafast_save_card") === "true";
	} catch {
		return false;
	}
});
const installmentsRef = useRef<number>(installments);
const creditTypeRef = useRef<string>(creditType);
const saveCardRef = useRef<boolean>(saveCard);
const paymentModeRef = useRef<"new" | "saved">(paymentMode);
const selectedSavedCardRef = useRef<DatafastSavedCard | null>(null);
const selectedCreditTypeLabel = CREDIT_TYPE_OPTIONS.find(({ value }) => value === creditType)?.label ?? 'Transacción corriente';
const datafastService = useRef(new DatafastService()).current;
const checkoutService = useRef(new CheckoutService()).current;
const selectedSavedCard = savedCards.find((card) => card.id === selectedSavedCardId) ?? null;

const CARD_HOLDER_FIELD: DatafastMissingField = {
	key: "cardHolder",
	label: "nombre en la tarjeta",
	selectors: [
		'input[name="card.holder"]',
		'.wpwl-control-cardHolder input',
		'.wpwl-control-cardHolder .wpwl-control',
	],
};

const readSaveCardPreferenceFromForm = (): boolean => {
	return saveCardRef.current;
};

const persistSaveCardPreference = (value: boolean) => {
	if (value !== saveCardRef.current) {
		saveCardRef.current = value;
		setSaveCard(value);
	} else {
		saveCardRef.current = value;
	}

	try {
		localStorage.setItem("datafast_save_card", value ? "true" : "false");
	} catch {
		// Ignore storage errors (e.g., private mode)
	}
};

const handleWidgetInstallmentsChange = (
	value: number,
	selectElement?: HTMLSelectElement
) => {
	if (!Number.isFinite(value)) {
		return;
	}

	const normalized = Number(value);
	if (installmentsRef.current === normalized) {
		return;
	}

	const previous = installmentsRef.current;
	const revertSelection = () => {
		if (selectElement) {
			selectElement.value = String(previous);
		}
	};

	const confirmed = window.confirm(
		"Cambiar el número de diferidos reiniciará el formulario de pago y deberás volver a ingresar los datos de la tarjeta. ¿Deseas continuar?"
	);

	if (!confirmed) {
		revertSelection();
		return;
	}

	handleStartPayment({ installments: normalized });
};

const handleWidgetCreditTypeChange = (
	value: string,
	selectElement?: HTMLSelectElement
) => {
	if (!value) {
		return;
	}

	const normalized = value.padStart(2, "0");
	if (creditTypeRef.current === normalized) {
		return;
	}

	const revertSelection = () => {
		if (selectElement) {
			selectElement.value = creditTypeRef.current;
		}
	};

	const confirmed = window.confirm(
		"Cambiar el tipo de crédito reiniciará el formulario de pago y deberás volver a ingresar los datos de la tarjeta. ¿Deseas continuar?"
	);

	if (!confirmed) {
		revertSelection();
		return;
	}

	handleStartPayment({ creditType: normalized });
};

const handleWidgetSaveCardToggle = (
	checked: boolean,
	inputElement?: HTMLInputElement
) => {
	if (saveCardRef.current === checked) {
		return;
	}

	const revertSelection = () => {
		if (inputElement) {
			inputElement.checked = saveCardRef.current;
		}
	};

	const confirmed = window.confirm(
		"Cambiar esta opción reiniciará el formulario de pago y deberás volver a ingresar los datos de la tarjeta. ¿Deseas continuar?"
	);

	if (!confirmed) {
		revertSelection();
		return;
	}

	handleStartPayment({ saveCard: checked });
};

const ensureCardholderFilled = (): boolean => {
	const form = document.querySelector("form.paymentWidgets");
	const holderInput = form?.querySelector<HTMLInputElement>('input[name="card.holder"]');

	if (!holderInput) {
		return true;
	}

	if (holderInput.value.trim() === "") {
		showInlineDatafastError("Completa el nombre en la tarjeta", CARD_HOLDER_FIELD);
		holderInput.focus();
		return false;
	}

	return true;
};

const isSavedCardFlow = () => paymentModeRef.current === "saved" && selectedSavedCardRef.current !== null;

const handleSelectSavedCardOption = (cardId: number) => {
	if (paymentMode === "saved" && selectedSavedCardId === cardId) {
		return;
	}

	if (widgetLoaded) {
		const confirmed = window.confirm(
			"Cambiar la tarjeta reiniciará el formulario de pago y deberás ingresar nuevamente la información. ¿Deseas continuar?"
		);
		if (!confirmed) {
			return;
		}
	}

	persistSaveCardPreference(false);
	saveCardRef.current = false;
	setSaveCard(false);
	const newlySelectedCard = savedCards.find((card) => card.id === cardId) ?? null;
	selectedSavedCardRef.current = newlySelectedCard;
	paymentModeRef.current = "saved";
	setSelectedSavedCardId(cardId);
	setPaymentMode("saved");
	if (showWidget) {
		void handleStartPayment();
	}
};

const handleUseNewCardOption = () => {
	if (paymentMode === "new") {
		return;
	}

	if (widgetLoaded) {
		const confirmed = window.confirm(
			"Mostrar el formulario para una nueva tarjeta reiniciará el proceso de pago. ¿Deseas continuar?"
		);
		if (!confirmed) {
			return;
		}
	}

	setPaymentMode("new");
	selectedSavedCardRef.current = null;
	paymentModeRef.current = "new";
	setSelectedSavedCardId(null);
	if (showWidget) {
		void handleStartPayment();
	}
};

	// ✅ VALIDAR QUE EXISTAN DATOS VÁLIDOS - NO FALLBACKS
	const [formData, setFormData] = useState<FormData>(() => {
		// SOLO usar CheckoutData validado - no fallbacks
		if (!checkoutData?.shippingData) {
			throw new Error("DatafastPaymentButton requiere CheckoutData válido");
		}

		const shippingData = checkoutData.shippingData;
		const nameParts = shippingData.name.split(' ');
		const firstName = nameParts[0];
		const lastName = nameParts.slice(1).join(' ');

		// ✅ CRÍTICO: Auto-extraer cédula desde RUC para Datafast
		const extractionResult = extractCedulaFromRUC(shippingData.identification);

		// Log para debugging
		console.log("🔍 DatafastPaymentButton: Procesando identificación:", {
			original: shippingData.identification,
			extracted: extractionResult.cedula,
			wasRuc: extractionResult.wasRuc,
			message: extractionResult.message
		});

		return {
			address: shippingData.street,
			city: shippingData.city,
			country: mapCountryCode(shippingData.country),
			given_name: firstName,
			middle_name: "",
			surname: lastName,
			phone: shippingData.phone,
			doc_id: extractionResult.cedula, // ✅ Usar cédula extraída automáticamente
		};
	});

	useEffect(() => {
		installmentsRef.current = installments;
	}, [installments]);

	useEffect(() => {
		creditTypeRef.current = creditType;
	}, [creditType]);

useEffect(() => {
	saveCardRef.current = saveCard;
}, [saveCard]);

useEffect(() => {
	paymentModeRef.current = paymentMode;
}, [paymentMode]);

useEffect(() => {
	selectedSavedCardRef.current = selectedSavedCard;
}, [selectedSavedCard]);

useEffect(() => {
	let isMounted = true;
	const fetchSavedCards = async () => {
		try {
			setSavedCardsLoading(true);
			const cards = await datafastService.getSavedCards();
			if (!isMounted) {
				return;
			}
			setSavedCards(cards);
			if (cards.length > 0) {
				const defaultCard = cards.find((card) => card.is_default) ?? cards[0];
				selectedSavedCardRef.current = defaultCard;
				paymentModeRef.current = "saved";
				setSelectedSavedCardId(defaultCard.id);
				setPaymentMode("saved");
				persistSaveCardPreference(false);
			}
		} finally {
			if (isMounted) {
				setSavedCardsLoading(false);
			}
		}
	};

	void fetchSavedCards();

	return () => {
		isMounted = false;
	};
}, [datafastService]);

useEffect(() => {
	if (installments === 0 && creditType !== "00") {
		setCreditType("00");
	} else if (installments > 0 && creditType === "00") {
		setCreditType("01");
	}
}, [installments, creditType]);
	useEffect(() => {
		return () => {
			const script = document.getElementById("datafast-widget-script");
			if (script) {
				script.remove();
			}
		};
	}, []);

	// ✅ VALIDACIÓN: CheckoutData no debe cambiar una vez pasado
	useEffect(() => {
		if (!checkoutData) {
			console.error("❌ DatafastPaymentButton: No se recibió CheckoutData válido");
		} else {
			console.log("✅ DatafastPaymentButton: Usando CheckoutData validado:", {
				sessionId: checkoutData.sessionId,
				userId: checkoutData.userId,
				total: checkoutData.totals.final_total,
				itemsCount: checkoutData.items.length,
				validatedAt: checkoutData.validatedAt
			});

			// ✅ AUTO-EXTRACCIÓN SILENCIOSA: No mostrar notificaciones innecesarias
			extractCedulaFromRUC(checkoutData.shippingData.identification);
		}
	}, [checkoutData, showNotification]);

	const handleInputChange = (field: keyof FormData, value: string) => {
		// ✅ CRÍTICO: Si está editando doc_id, aplicar auto-extracción de cédula desde RUC
		if (field === "doc_id") {
			const extractionResult = extractCedulaFromRUC(value);

			// ✅ AUTO-EXTRACCIÓN SILENCIOSA: Solo mostrar errores críticos
			if (extractionResult.message && !extractionResult.wasRuc && extractionResult.cedula.length !== 10) {
				// Solo mostrar errores de validación críticos
				showNotification(
					NotificationType.WARNING,
					extractionResult.message
				);
			}

			setFormData((prev) => ({...prev, [field]: extractionResult.cedula}));
		} else {
			setFormData((prev) => ({...prev, [field]: value}));
		}
	};

	const validateFormData = (): boolean => {
		const requiredFields: (keyof FormData)[] = [
			"address",
			"city",
			"country",
			"given_name",
			"surname",
			"phone",
			"doc_id",
		];

		for (const field of requiredFields) {
			if (!formData[field] || formData[field].trim() === "") {
				showNotification(
					NotificationType.ERROR,
					`El campo ${field.replace("_", " ")} es obligatorio`
				);
				return false;
			}
		}

		if (formData.doc_id.length !== 10 || !/^\d+$/.test(formData.doc_id)) {
			showNotification(
				NotificationType.ERROR,
				"La cédula debe tener exactamente 10 dígitos"
			);
			return false;
		}

		if (!INSTALLMENT_OPTIONS.includes(installments)) {
			showNotification(
				NotificationType.ERROR,
				"Selecciona un número de diferidos válido"
			);
			return false;
		}

		const creditTypeValid = CREDIT_TYPE_OPTIONS.some(({ value }) => value === creditType);
		if (!creditTypeValid) {
			showNotification(
				NotificationType.ERROR,
				"Selecciona un tipo de crédito válido"
			);
			return false;
		}

		return true;
	};

	const handleStartPayment = async (overrides?: { installments?: number; creditType?: string; saveCard?: boolean }) => {
		if (!cart || cart.items.length === 0) {
			showNotification(NotificationType.ERROR, "El carrito está vacío");
			return;
		}

		if (!validateFormData()) {
			return;
		}

		const activeSavedCard = selectedSavedCardRef.current;
		const savedCardFlowActive = isSavedCardFlow();
		const effectiveInstallments = overrides?.installments ?? installmentsRef.current ?? installments;
		const effectiveCreditType = overrides?.creditType ?? creditTypeRef.current ?? creditType;
		const effectiveSaveCard = savedCardFlowActive
			? false
			: overrides?.saveCard ?? saveCardRef.current ?? saveCard;
		const shouldUpdateState = overrides !== undefined;

		if (shouldUpdateState) {
			if (overrides?.installments !== undefined) {
				setInstallments(overrides.installments);
				installmentsRef.current = overrides.installments;
			}
			if (overrides?.creditType) {
				setCreditType(overrides.creditType);
				creditTypeRef.current = overrides.creditType;
			}
			if (overrides?.saveCard !== undefined) {
				persistSaveCardPreference(overrides.saveCard);
			}
		}

		setIsLoading(true);
		setWidgetLoaded(false);

		try {
			// ✅ VALIDAR QUE EXISTE CHECKOUTDATA
			if (!checkoutData) {
				throw new Error("No se puede proceder sin CheckoutData validado");
			}

			// ✅ USAR DATOS DIRECTAMENTE DEL OBJETO TEMPORAL - NO RECALCULAR
			console.log("💰 Usando totales pre-calculados de CheckoutData:", checkoutData.totals);
			console.log("🛒 Usando items pre-preparados de CheckoutData:", checkoutData.items);

			// Almacenar totales para usar en el widget
			setCalculatedTotals(checkoutData.totals);

			// ✅ USAR DATOS VALIDADOS DEL CHECKOUT - NO FORMULARIO
			const customerData: any = {
				given_name: formData.given_name,
				surname: formData.surname,
				phone: formData.phone,
				doc_id: formData.doc_id,
			};

			// Solo agregar middle_name si realmente tiene valor
			if (formData.middle_name && formData.middle_name.trim() !== "") {
				customerData.middle_name = formData.middle_name.trim();
			}

			const requestData: DatafastCheckoutRequest = {
				shippingAddress: {
					street: formData.address,
					city: formData.city,
					country: mapCountryCode(formData.country),
				},
				customer: customerData,
				items: checkoutData.items, // ✅ USAR ITEMS DEL OBJETO TEMPORAL
				total: checkoutData.totals.final_total, // ✅ USAR TOTAL DEL OBJETO TEMPORAL
				subtotal: checkoutData.totals.subtotal_with_discounts,
				shipping_cost: checkoutData.totals.shipping_cost,
				tax: checkoutData.totals.iva_amount,
				discount_code: checkoutData.discountCode || null,
				discount_info: checkoutData.discountInfo || null,
				installments: effectiveInstallments,
				credit_type: effectiveCreditType,
				save_card: effectiveSaveCard,
				// ✅ NUEVO: Incluir información de sesión para validación
				session_id: checkoutData.sessionId,
				validated_at: checkoutData.validatedAt
			};

			if (savedCardFlowActive && activeSavedCard) {
				requestData.payment = {
					method: "datafast",
					registration_id: activeSavedCard.registration_id,
				};
			} else if (effectiveSaveCard) {
				requestData.payment = {
					method: "datafast",
				};
			}

			console.log("Iniciando checkout con Datafast usando CheckoutData...", requestData);
			console.log("💰 Total enviado a Datafast: $", checkoutData.totals.final_total);
			console.log("🔍 DEBUG - Datos de CheckoutData enviados:");
			console.log("   - sessionId:", checkoutData.sessionId);
			console.log("   - customer.middle_name:", requestData.customer?.middle_name !== undefined
				? `"${requestData.customer.middle_name}" (tipo: ${typeof requestData.customer.middle_name})`
				: "OMITIDO - campo no enviado");
			console.log("   - shippingAddress.country:", `"${requestData.shippingAddress.country}" (tipo: ${typeof requestData.shippingAddress.country})`);
			console.log("🔍 DEBUG - Customer completo:", JSON.stringify(requestData.customer, null, 2));

			const response = await datafastService.createCheckout(requestData);
			console.log("Respuesta del checkout:", response);

			if (response.status === "success" && response.data) { // ✅ CORREGIDO: Cambiar response.success por response.status
				setCheckoutResponse(response.data);
				if (typeof response.data.installments === "number") {
					setInstallments(response.data.installments);
				}
				if (typeof response.data.credit_type === "string") {
					setCreditType(response.data.credit_type);
				}
				setShowForm(false);
				setShowWidget(true);

				// ✅ GUARDAR DATOS NECESARIOS PARA LA VERIFICACIÓN
				localStorage.setItem(
					"datafast_transaction_id",
					response.data.transaction_id
				);
				
				// ✅ GUARDAR CHECKOUT_ID PARA SIMULACIÓN
				localStorage.setItem(
					"datafast_checkout_id",
					response.data.checkout_id
				);
				
				// ✅ GUARDAR CHECKOUTDATA COMPLETO PARA PROCESAR LA ORDEN DESPUÉS
				localStorage.setItem("datafast_calculated_total", checkoutData.totals.final_total.toString());
				localStorage.setItem("datafast_form_data", JSON.stringify({
					...formData,
					installments: effectiveInstallments,
					credit_type: effectiveCreditType,
				}));
				localStorage.setItem("datafast_installments", String(effectiveInstallments));
				localStorage.setItem("datafast_credit_type", effectiveCreditType);
				localStorage.setItem("datafast_save_card", effectiveSaveCard ? "true" : "false");

				// ✅ GUARDAR CHECKOUTDATA COMPLETO EN LUGAR DEL CARRITO
				console.log("💾 GUARDANDO CHECKOUTDATA VALIDADO EN LOCALSTORAGE");
				console.log("   - Items en CheckoutData:", checkoutData.items.length);
				console.log("   - SessionId:", checkoutData.sessionId);
				localStorage.setItem("datafast_checkout_data", JSON.stringify(checkoutData));
				console.log("   ✅ CheckoutData guardado en 'datafast_checkout_data'");

				// ✅ CRITICAL FIX: Guardar sessionId que espera el backend
				localStorage.setItem("datafast_session_id", checkoutData.sessionId);
				console.log("   ✅ SessionId guardado para backend:", checkoutData.sessionId);

				showNotification(
					NotificationType.SUCCESS,
					"Checkout creado. Preparando formulario de pago..."
				);

				setTimeout(() => {
					if (response.data) {
						void loadDatafastWidget(response.data.checkout_id);
					} else {
						showNotification(
							NotificationType.ERROR,
							"Datos de checkout no disponibles"
						);
					}
				}, 100);
			} else {
				throw new Error(response.message || "Error al crear checkout");
			}
		} catch (error) {
			console.error("Error al iniciar pago con Datafast:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Error desconocido";
			showNotification(NotificationType.ERROR, errorMessage);
			onError?.(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	const loadDatafastWidget = async (checkoutId: string) => {
		try {
			console.log("Cargando widget de Datafast...", checkoutId);

			const existingScript = document.getElementById("datafast-widget-script");
			if (existingScript) {
				existingScript.remove();
			}

			try {
				await ensureGlobalJQuery();
				console.log("✅ jQuery disponible para opciones avanzadas de Datafast");
			} catch (jqueryError) {
				console.warn("⚠️ Continuando sin jQuery explícito. Revisar certificación si es obligatorio.", jqueryError);
			}

			const shouldUseSavedCard = isSavedCardFlow();

			const widgetOptions: Record<string, any> = {
				onReady: function () {
					const savedCardActive = isSavedCardFlow();
					console.log("✅ Widget de Datafast listo!");
					ensureDatafastWidgetSelectors(
						installmentsRef.current,
						creditTypeRef.current,
						handleWidgetInstallmentsChange,
						handleWidgetCreditTypeChange
					);
					scheduleRestrictedFieldDedupe();
					insertCertificationBadge();
					setTimeout(insertCertificationBadge, 150);
					const hideSaveCardUI = () => {
						const form = document.querySelector('form.paymentWidgets');
						const saveGroup = form?.querySelector<HTMLElement>('.bcommerce-save-card-group');
						if (saveGroup) {
							saveGroup.style.display = "none";
						}
					};
					if (savedCardActive) {
						hideSaveCardUI();
						setTimeout(() => {
							ensureDatafastWidgetSelectors(
								installmentsRef.current,
								creditTypeRef.current,
								handleWidgetInstallmentsChange,
								handleWidgetCreditTypeChange
							);
							scheduleRestrictedFieldDedupe();
							hideSaveCardUI();
						}, 100);
					} else {
						ensureDatafastSaveCardOption(saveCardRef.current, handleWidgetSaveCardToggle);
						setTimeout(() => {
							ensureDatafastWidgetSelectors(
								installmentsRef.current,
								creditTypeRef.current,
								handleWidgetInstallmentsChange,
								handleWidgetCreditTypeChange
							);
							scheduleRestrictedFieldDedupe();
							ensureDatafastSaveCardOption(saveCardRef.current, handleWidgetSaveCardToggle);
						}, 100);
					}
					setWidgetLoaded(true);
					clearInlineDatafastError();
					showNotification(
						NotificationType.SUCCESS,
						"Formulario de pago cargado correctamente."
					);
					
					// Agregar listener adicional al formulario cuando esté listo
					setTimeout(() => {
						const form = document.querySelector('form.paymentWidgets');
						if (form) {
							console.log("📝 Agregando listener adicional al formulario");
							
							// Escuchar el evento submit
							form.addEventListener('submit', function(_e) {
								console.log("🚀 FORMULARIO ENVIADO - Evento capturado!");
								console.log("   - Action actual:", form.getAttribute('action'));
								console.log("   - Method:", form.getAttribute('method'));
								console.log("   - Target:", form.getAttribute('target'));
								
								// No prevenir el default, solo observar
								// _e.preventDefault();
							});
							
							// También observar cambios en el DOM
							const observer = new MutationObserver((mutations) => {
								mutations.forEach((mutation) => {
									if (mutation.type === 'attributes' && mutation.attributeName === 'action') {
										console.log("🔄 Action del formulario cambió a:", form.getAttribute('action'));
									}
								});
								scheduleRestrictedFieldDedupe();
							});
							
							observer.observe(form, { 
								attributes: true, 
								attributeFilter: ['action', 'method'] 
							});
							
							// Observar la creación de iframes
							const iframeObserver = new MutationObserver((mutations) => {
								mutations.forEach((mutation) => {
									mutation.addedNodes.forEach((node: any) => {
										if (node.tagName === 'IFRAME') {
											console.log("🖼️ Nuevo iframe detectado:", {
												name: node.name,
												src: node.src,
												id: node.id
											});
											
											// Escuchar cuando el iframe se carga
											node.addEventListener('load', () => {
												console.log("✅ Iframe cargado:", node.name);
												try {
													const iframeDoc = node.contentDocument || node.contentWindow?.document;
													if (iframeDoc) {
														console.log("📄 Contenido del iframe accesible");
													}
												} catch (e) {
													console.log("🔒 Iframe cross-origin, no se puede acceder al contenido");
												}
											});
										}
									});
								});
								scheduleRestrictedFieldDedupe();
							});
							
							iframeObserver.observe(document.body, { 
								childList: true, 
								subtree: true 
							});
						}
					}, 500);
				},
				onBeforeSubmitCard: function (data: any) {
					console.log("🔄 Widget Datafast: Usuario hizo clic en Pagar, procesando...");
					console.log("🔍 Datos de la tarjeta que se están enviando:", data);

					if (isSavedCardFlow()) {
						clearInlineDatafastError();
						showNotification(
							NotificationType.INFO,
							"Procesando pago con tu tarjeta guardada..."
						);
						return true;
					}

					const validation = validateDatafastCardFields(data);
					if (!validation.valid) {
						const firstMissing = validation.missingFields[0];
						const message = `Completa el ${firstMissing.label} para continuar`;
						console.warn("⚠️ Validación Datafast: faltan campos obligatorios:", validation.missingFields);
						showInlineDatafastError(message, firstMissing);
						focusFirstMissingDatafastField(validation.missingFields);
						return false;
					}

					const currentPreference = readSaveCardPreferenceFromForm();
					persistSaveCardPreference(currentPreference);

					if (!ensureCardholderFilled()) {
						return false;
					}

					dedupeRestrictedDatafastFields();
					scheduleRestrictedFieldDedupe();

					clearInlineDatafastError();
					
					// Buscar el formulario y verificar su estado
					const form = document.querySelector('form.paymentWidgets') as HTMLFormElement;
					if (form) {
						console.log("📝 Estado del formulario al hacer submit:");
						console.log("   - Action:", form.action);
						console.log("   - Method:", form.method);
						console.log("   - Target:", form.target);
						
						// Verificar si hay campos ocultos con el checkout ID
						const checkoutIdField = form.querySelector('input[name="checkoutId"]');
						if (checkoutIdField) {
							console.log("   - CheckoutId field:", checkoutIdField.getAttribute('value'));
						}
						
						// Verificar si hay un iframe target
						const targetFrame = document.querySelector(`iframe[name="${form.target}"]`);
						if (targetFrame) {
							console.log("   - Target iframe encontrado:", targetFrame);
						} else {
							console.log("   - ⚠️ No se encontró iframe target");
						}
					}
					
					showNotification(
						NotificationType.INFO,
						"Procesando pago con Datafast..."
					);
					
					// Retornar true para permitir el submit
					return true;
				},
				onAfterSubmitCard: async function (data: any) {
					console.log("⏳ Widget Datafast: Datos enviados, esperando respuesta...");
					console.log("📤 Respuesta completa después del envío:", data);
					
					// Debug detallado de la respuesta
					console.log("🔍 DEBUG: Analizando estructura de la respuesta:");
					console.log("   - Tipo de data:", typeof data);
					console.log("   - Es un array?:", Array.isArray(data));
					console.log("   - Keys del objeto:", data ? Object.keys(data) : "data es null/undefined");
					
					// Si data es un evento, intentar obtener más información
					if (data && data.originalEvent) {
						console.log("📋 Evento original detectado:", data.originalEvent);
					}
					if (data && data.target) {
						console.log("🎯 Target del evento:", data.target);
						const form = data.target;
						if (form && form.tagName === 'FORM') {
							console.log("📝 Formulario encontrado en evento:");
							console.log("   - Action:", form.action);
							console.log("   - Method:", form.method);
						}
					}
					
					// Intentar convertir a string solo si no es circular
					try {
						console.log("   - JSON stringified:", JSON.stringify(data, null, 2));
					} catch (e) {
						console.log("   - No se puede convertir a JSON (referencia circular)");
					}
					
					// Verificar si hay información de redirect
					if (data && data.redirect) {
						console.log("🔗 Información de redirect encontrada:", data.redirect);
					}
					
					// Verificar si hay un resourcePath
					if (data && data.resourcePath) {
						console.log("📍 ResourcePath encontrado:", data.resourcePath);
						console.log("🔄 Intentando redirección manual a:", `/datafast-result?resourcePath=${encodeURIComponent(data.resourcePath)}`);
						
						// Intentar redirección manual si el widget no lo hace automáticamente
						setTimeout(() => {
							console.log("⏰ Esperando 3 segundos para ver si hay redirección automática...");
						}, 3000);
					}
					
					// Monitorear cambios en el DOM y verificar estado
					let checkCount = 0;
					const maxChecks = 10; // Verificar máximo 10 veces
					
					const checkInterval = setInterval(async () => {
						checkCount++;
						console.log(`🔄 Verificación ${checkCount}/${maxChecks} del estado del pago...`);
						
						// Verificar si hay un iframe de pago
						const paymentIframe = document.querySelector('iframe[name*="oppwa"], iframe[src*="oppwa"], iframe[src*="datafast"]') as HTMLIFrameElement;
						if (paymentIframe) {
							console.log("🖼️ iFrame de pago detectado:", {
								name: paymentIframe.getAttribute('name'),
								src: paymentIframe.getAttribute('src'),
								visible: paymentIframe.style.display !== 'none'
							});
						}
						
						// Verificar si el formulario cambió
						const form = document.querySelector('form.paymentWidgets');
						if (form) {
							const formAction = form.getAttribute('action');
							if (formAction && formAction.includes('resourcePath')) {
								console.log("📝 Formulario actualizado con resourcePath:", formAction);
								clearInterval(checkInterval);
								
								// Extraer resourcePath
								const urlParams = new URLSearchParams(formAction.split('?')[1]);
								const resourcePath = urlParams.get('resourcePath');
								if (resourcePath) {
									console.log("🎯 ResourcePath extraído:", resourcePath);
									localStorage.setItem("datafast_resource_path", resourcePath);
									
									// Redirigir manualmente después de un momento
									setTimeout(() => {
										console.log("🚀 Redirigiendo manualmente al resultado...");
										window.location.href = formAction;
									}, 2000);
								}
							}
						}
						
						// Verificar el estado del pago con el backend
						if (checkCount >= 3) { // Empezar a verificar después del 3er intento
							const checkoutId = localStorage.getItem("datafast_checkout_id");
							const transactionId = localStorage.getItem("datafast_transaction_id");
							
							if (checkoutId && transactionId) {
								try {
									const verifyResponse = await fetch(`/api/datafast/verify-payment/${transactionId}`, {
										method: 'GET',
										headers: {
											'Content-Type': 'application/json',
											'Authorization': `Bearer ${localStorage.getItem('authToken')}`
										}
									});
									
									const result = await verifyResponse.json();
									console.log(`📊 Estado del pago (intento ${checkCount}):`, result.data?.payment_status);
									
									if (result.status === NotificationType.SUCCESS && result.data?.payment_status === 'completed') {
										console.log("🎉 Pago completado exitosamente!");
										clearInterval(checkInterval);
										window.location.href = `/datafast-result?status=success&transactionId=${transactionId}`;
									}
								} catch (error) {
									console.error(`❌ Error verificando estado (intento ${checkCount}):`, error);
								}
							}
						}
						
						// Detener después de maxChecks intentos
						if (checkCount >= maxChecks) {
							console.log("⏰ Se alcanzó el límite de verificaciones");
							clearInterval(checkInterval);
						}
					}, 3000); // Verificar cada 3 segundos
				},
				onLoadThreeDSecure: function () {
					console.log("🔐 Widget Datafast: Cargando 3D Secure...");
				},
				onBeforeRedirectToResult: function (data: any) {
					console.log("🔄 Widget Datafast: Redirigiendo a página de resultado...");
					console.log("🔗 Datos completos de redirección:", data);
					console.log("🔍 DEBUG Redirect - Keys:", data ? Object.keys(data) : "null");
					console.log("🔍 DEBUG Redirect - JSON:", JSON.stringify(data, null, 2));
					
					// Si encontramos un resourcePath, guardarlo
					if (data && data.resourcePath) {
						localStorage.setItem("datafast_resource_path", data.resourcePath);
						console.log("💾 ResourcePath guardado en localStorage:", data.resourcePath);
					}
					
					// Permitir que el widget haga la redirección
					return true;
				},
				onChangeBrand: function (e: any) {
					console.log("💳 Widget Datafast: Marca de tarjeta detectada:", e.brand);
					console.log("💳 Datos completos del evento:", e);
				},
				onError: function (error: any) {
					console.error("❌ Widget Datafast: Error en el pago", error);
					console.error("❌ Detalles completos del error:", {
						message: error.message,
						code: error.code,
						name: error.name,
						full: error
					});
					showNotification(
						NotificationType.ERROR,
						"Error al procesar el pago: " + (error.message || error.code || 'Error desconocido')
					);
				},
				onSubmit: function (data: any) {
					console.log("📋 Widget Datafast: Formulario enviado");
					console.log("📋 Datos completos del formulario:", data);
					console.log("📋 DEBUG Submit - Keys:", data ? Object.keys(data) : "null");
					console.log("📋 DEBUG Submit - JSON:", JSON.stringify(data, null, 2));
					
					// Verificar el formulario actual
					const form = document.querySelector('form.paymentWidgets');
					if (form) {
						console.log("📝 Formulario encontrado:");
						console.log("   - Action:", form.getAttribute('action'));
						console.log("   - Method:", form.getAttribute('method'));
						console.log("   - Target:", form.getAttribute('target'));
						
						// Verificar si hay un iframe oculto
						const iframe = document.querySelector('iframe[name*="oppwa"]');
						if (iframe) {
							console.log("🖼️ iFrame de pago encontrado:", iframe);
						}
					}
					
					return true; // Permitir el submit
				},
				style: "card",
				locale: "es",
				labels: {
					cvv: "CVV",
					cardHolder: "Nombre (igual que en la tarjeta)",
				},
			};

			if (shouldUseSavedCard) {
				widgetOptions.registrations = {
					requireCvv: true,
					hideInitialPaymentForms: true,
				};
			}

			(window as any).wpwlOptions = widgetOptions;

			// Cargar script adicional de validaciones de Datafast (opcional, ya que puede no existir)
			const validationScriptUrl = import.meta.env.VITE_DATAFAST_VALIDATION_SCRIPT;
			if (validationScriptUrl) {
				const additionalScript = document.createElement("script");
				additionalScript.src = validationScriptUrl;
				additionalScript.async = true;
				additionalScript.onerror = () => {
					console.warn("Script de validaciones adicionales de Datafast no disponible, continuando sin él");
				};
				document.head.appendChild(additionalScript);
			}

			// Usar la URL del widget que viene del backend (incluye el checkoutId)
			const script = document.createElement("script");
			script.id = "datafast-widget-script";
			
			// IMPORTANTE: Usar widget_url del backend que ya incluye el checkoutId
			if (checkoutResponse?.widget_url) {
				script.src = checkoutResponse.widget_url;
				console.log("📌 Usando widget_url del backend:", checkoutResponse.widget_url);
			} else {
				// Fallback solo si no hay widget_url del backend. Usar env y, por defecto, host productivo.
				const widgetBaseUrl =
					import.meta.env.VITE_DATAFAST_WIDGET_URL || "https://eu-prod.oppwa.com/v1/paymentWidgets.js";
				script.src = `${widgetBaseUrl}?checkoutId=${checkoutId}`;
				console.log("⚠️ Usando fallback URL con checkoutId:", script.src);
			}
			
			script.async = true;

				script.onload = () => {
					console.log("✅ Script de widget Datafast cargado exitosamente");
					
					// Verificar que el widget se cargó correctamente
					setTimeout(() => {
						const savedCardActive = isSavedCardFlow();
						ensureDatafastWidgetSelectors(
							installmentsRef.current,
							creditTypeRef.current,
							handleWidgetInstallmentsChange,
							handleWidgetCreditTypeChange
						);
						scheduleRestrictedFieldDedupe();
						if (savedCardActive) {
							const saveGroup = document.querySelector<HTMLElement>('.bcommerce-save-card-group');
							if (saveGroup) {
								saveGroup.style.display = "none";
							}
						} else {
							ensureDatafastSaveCardOption(saveCardRef.current, handleWidgetSaveCardToggle);
						}
						const forms = document.querySelectorAll('form.paymentWidgets');
						console.log("📝 Formularios de pago encontrados:", forms.length);
					
					if (forms.length === 0) {
						console.warn("⚠️ No se encontraron formularios con clase 'paymentWidgets'");
					} else {
						forms.forEach((form, index) => {
							console.log(`📋 Formulario ${index + 1}:`, {
								action: form.getAttribute('action'),
								method: form.getAttribute('method'),
								'data-brands': form.getAttribute('data-brands')
							});
						});
					}
					
					// Verificar si hay scripts dinámicos bloqueados
					const scripts = document.querySelectorAll('script[src*="techlab-cdn"], script[src*="oppwa"]');
					console.log("📜 Scripts de Datafast cargados:", scripts.length);
					scripts.forEach(script => {
						console.log("  - ", (script as HTMLScriptElement).src);
					});
				}, 1000);
			};

			script.onerror = (error) => {
				console.error("❌ Error al cargar script del widget:", error);
				showNotification(
					NotificationType.ERROR,
					"Error al cargar el formulario de pago. Por favor, recargue la página."
				);
			};

			document.head.appendChild(script);
			console.log("📌 Script del widget añadido al DOM:", script.src);
		} catch (error) {
			console.error("Error al configurar widget:", error);
			showNotification(
				NotificationType.ERROR,
				"Error al configurar el formulario de pago"
			);
		}
	};

	const handleCompleteTestCheckout = async () => {
		console.log("🧪 DatafastPaymentButton.handleCompleteTestCheckout INICIADO");
	
		if (!cart || cart.items.length === 0) {
			console.log("❌ Carrito vacío o null, abortando checkout");
			showNotification(NotificationType.ERROR, "El carrito está vacío");
			return;
		}

		if (!validateFormData()) {
			console.log("❌ Validación de formulario falló");
			return;
		}

		console.log("🛒 ANÁLISIS COMPLETO DEL CARRITO ANTES DEL CHECKOUT (DATAFAST):");
		console.log("📊 Cart completo:", JSON.stringify(cart, null, 2));
		console.log("📊 Total de items en carrito:", cart.items.length);
		console.log("📊 Total del carrito:", cart.total);

		cart.items.forEach((item, index) => {
			console.log(`📋 Item ${index + 1}:`, {
				id: item.id,
				productId: item.productId,
				quantity: item.quantity,
				price: item.price,
				subtotal: item.subtotal,
				product: item.product ? {
					id: item.product.id,
					name: item.product.name,
					price: item.product.price,
					final_price: item.product.final_price,
					sellerId: item.product.sellerId,
					seller_id: item.product.seller_id,
					user_id: item.product.user_id
				} : null,
				completeItem: item
			});
		});

		console.log("🔍 VERIFICANDO DUPLICADOS EN EL CARRITO (DATAFAST):");
		const itemsByProductId = cart.items.reduce((acc: any, item, index) => {
			if (!acc[item.productId]) {
				acc[item.productId] = [];
			}
			acc[item.productId].push({index, item});
			return acc;
		}, {});

		console.log("📊 Items agrupados por productId:", itemsByProductId);

		Object.keys(itemsByProductId).forEach(productId => {
			const items = itemsByProductId[productId];
			if (items.length > 1) {
				console.warn(`⚠️ DUPLICADO EN CARRITO DETECTADO para productId ${productId}:`);
				console.warn(`❌ Se encontraron ${items.length} items para el mismo producto`);
				items.forEach((itemData: any, i: number) => {
					console.warn(`   ${i + 1}. Item[${itemData.index}]:`, itemData.item);
				});
			} else {
				console.log(`✅ Producto ${productId}: Sin duplicados (${items[0].item.quantity} unidades)`);
			}
		});

		setIsLoading(true);

		try {
			console.log("🚀 Iniciando checkout de prueba completo (DATAFAST)...");

			// ✅ CORREGIDO: Agregar null check para cart
			const sellerId = cart ? CheckoutService.getSellerIdFromCart(cart) : null;
			console.log("🏪 Seller ID obtenido (DATAFAST):", sellerId);

			// ✅ USAR MISMA LÓGICA DE CÁLCULO QUE CHECKOUT PAGE
			const checkoutItems = await CheckoutItemsService.prepareItemsForCheckout(cart.items);
			console.log("🛒 Items formateados con descuentos aplicados (DATAFAST):", JSON.stringify(checkoutItems, null, 2));

			// ✅ CALCULAR TOTALES CORRECTOS PARA EL BACKEND
			console.log("🔍 DEBUG: cart.items antes de calcular:", cart.items);
			console.log("🔍 DEBUG: appliedDiscount antes de calcular:", appliedDiscount);
			
			const calculatedTotals = await CheckoutItemsService.calculateCheckoutTotals(cart.items, appliedDiscount);
			console.log("💰 Totales calculados para backend (DATAFAST):", calculatedTotals);
			console.log("🔍 DEBUG: calculatedTotals es null?", calculatedTotals === null);
			console.log("🔍 DEBUG: calculatedTotals es undefined?", calculatedTotals === undefined);
			console.log("🔍 DEBUG: tipo de calculatedTotals:", typeof calculatedTotals);

			console.log("🛒 Items formateados para backend (DATAFAST):", JSON.stringify(checkoutItems, null, 2));

			// ✅ VALIDACIÓN ESTRICTA - NO FALLBACKS
			if (!formData.address || formData.address.trim() === '') {
				throw new Error('Dirección es obligatoria para procesar el pago');
			}
			if (!formData.city || formData.city.trim() === '') {
				throw new Error('Ciudad es obligatoria para procesar el pago');
			}
			if (!formData.country || formData.country.trim() === '') {
				throw new Error('País es obligatorio para procesar el pago');
			}
			if (!formData.phone || formData.phone.trim() === '') {
				throw new Error('Teléfono es obligatorio para procesar el pago');
			}
			if (!calculatedTotals) {
				throw new Error('No se pudieron calcular los totales del pedido');
			}
			if (calculatedTotals.total <= 0) {
				throw new Error('El total del pedido debe ser mayor a $0');
			}

			const testCheckoutData = {
				payment: {
					method: "datafast" as PaymentMethod,
				} as PaymentInfo,
				shippingAddress: {
					name: formData.given_name + " " + formData.surname,
					street: formData.address,
					city: formData.city,
					state: formData.country,
					postalCode: "170000",
					country: formData.country,
					phone: formData.phone,
				},
				seller_id: sellerId || undefined,
				items: checkoutItems,
				// ✅ STRICT: Usar totales calculados SIN fallbacks
				calculated_totals: {
					subtotal: calculatedTotals.subtotal,
					tax: calculatedTotals.tax,
					shipping: calculatedTotals.shipping,
					total: calculatedTotals.total,
					total_discounts: calculatedTotals.totalDiscounts
				},
				discount_code: appliedDiscount?.discountCode.code || null,
				discount_info: appliedDiscount || null
			};

			console.log("📦 Datos completos de checkout (DATAFAST):", JSON.stringify(testCheckoutData, null, 2));
			console.log("🔍 DEBUG: testCheckoutData.calculated_totals:", testCheckoutData.calculated_totals);
			console.log("🔍 DEBUG: Estructura del objeto calculated_totals:");
			console.log("   - subtotal:", testCheckoutData.calculated_totals.subtotal);
			console.log("   - tax:", testCheckoutData.calculated_totals.tax);  
			console.log("   - shipping:", testCheckoutData.calculated_totals.shipping);
			console.log("   - total:", testCheckoutData.calculated_totals.total);
			console.log("   - total_discounts:", testCheckoutData.calculated_totals.total_discounts);
			// ✅ SOLUCIÓN CORREGIDA: Replicar EXACTAMENTE el flujo del widget real
			console.log("🎯 PRUEBA COMPLETA DATAFAST: Simulando flujo idéntico al widget real");

			// Paso 1: Crear checkout (igual que el widget)
			await handleStartPayment();

			// Paso 2: Esperar a que se complete la creación del checkout
			setTimeout(async () => {
				try {
					// Obtener datos del checkout creado
					const transactionId = localStorage.getItem("datafast_transaction_id");
					const checkoutId = localStorage.getItem("datafast_checkout_id");
					
					if (!transactionId || !checkoutId) {
						throw new Error("No se encontraron IDs de transacción para continuar con la simulación");
					}

					console.log("🔄 Simulando flujo completo del widget (sin usar crédito)...");
					
					// Paso 3: Simular el comportamiento EXACTO del widget real
					const resultUrl = await datafastService.simulateCompleteWidgetFlow(
						checkoutId,
						transactionId,
						calculatedTotals.total,
						formData
					);

					console.log("✅ Flujo del widget simulado exitosamente");
					console.log("🚀 Redirigiendo a DatafastResultPage (idéntico al widget real):", resultUrl);

					// Paso 4: Redirigir EXACTAMENTE igual que lo haría el widget real
					// Esto procesará el resultado a través de DatafastResultPage usando el flujo completo
					window.location.href = resultUrl;
					
					// Ya no necesitamos limpiar el carrito aquí - DatafastResultPage se encargará
					
				} catch (simulationError) {
					console.error("❌ Error en simulación de widget flow:", simulationError);
					showNotification(
						NotificationType.ERROR, 
						"Error al simular el flujo del widget: " + (simulationError as any)?.message
					);
				}
			}, 2000); // Tiempo para que se complete el checkout

			// Salir aquí - el flujo continuará en DatafastResultPage
			return;
		} catch (error) {
			console.error("❌ Error COMPLETO en el checkout de prueba (DATAFAST):");
			console.error("📊 Error object:", error);
			console.error("📊 Error stack:", (error as any)?.stack);
			const errorMessage =
				error instanceof Error ? error.message : "Error al procesar el checkout de prueba";
			console.error("📊 Error message final:", errorMessage);
			showNotification(NotificationType.ERROR, errorMessage);
			onError?.(errorMessage);
		} finally {
			setIsLoading(false);
			console.log("🧪 DatafastPaymentButton.handleCompleteTestCheckout FINALIZADO");
		}
	};

	const handleSimulatePaymentResult = async () => {
		if (!checkoutResponse) {
			showNotification(NotificationType.ERROR, "No hay datos de checkout");
			return;
		}

		setIsLoading(true);
		try {
			console.log("Simulando pago exitoso completo...");

			// ✅ STRICT: Validar datos antes de proceder
			if (!cart || cart.items.length === 0) {
				throw new Error('Carrito vacío - no se puede procesar el pago');
			}

			const totals = await CheckoutItemsService.calculateCheckoutTotals(cart.items, appliedDiscount);
			if (!totals || totals.total <= 0) {
				throw new Error('No se pudieron calcular totales válidos para el pago');
			}

			const verifyResponse = await datafastService.simulateSuccessfulPayment(
				checkoutResponse.checkout_id,
				checkoutResponse.transaction_id,
				totals.total
			);

			console.log("Respuesta de verificación Datafast:", verifyResponse);

			if (verifyResponse.success && verifyResponse.data) {
				console.log("Procesando checkout completo en el sistema...");
				
				// ✅ CORREGIDO: Agregar null check para cart
				const sellerId = cart ? CheckoutService.getSellerIdFromCart(cart) : null;
				console.log("Seller ID obtenido:", sellerId);
				
				// ✅ USAR MISMA LÓGICA DE CÁLCULO QUE CHECKOUT PAGE
				const items = cart ? await CheckoutItemsService.prepareItemsForCheckout(cart.items) : [];
				
				// ✅ CALCULAR TOTALES CORRECTOS PARA EL BACKEND
				console.log("🔍 DEBUG SIMULATE: cart.items antes de calcular:", cart?.items);
				console.log("🔍 DEBUG SIMULATE: appliedDiscount antes de calcular:", appliedDiscount);
				
				const calculatedTotals = cart ? await CheckoutItemsService.calculateCheckoutTotals(cart.items, appliedDiscount) : null;
				console.log("💰 DEBUG SIMULATE: Totales calculados:", calculatedTotals);
				console.log("🔍 DEBUG SIMULATE: calculatedTotals es null?", calculatedTotals === null);
				console.log("🔍 DEBUG SIMULATE: calculatedTotals es undefined?", calculatedTotals === undefined);
				
				const checkoutRequestData = {
					payment: {
						method: "datafast" as PaymentMethod,
					} as PaymentInfo,
					shippingAddress: {
						name: formData.given_name + " " + formData.surname,
						street: formData.address,
						city: formData.city,
						state: formData.country,
						postalCode: "00000",
						country: formData.country,
						phone: formData.phone,
					},
					seller_id: sellerId || undefined,
					items: items,
					// ✅ STRICT: Usar totales calculados SIN validación ternaria (with null check)
					calculated_totals: calculatedTotals ? {
						subtotal: calculatedTotals.subtotal,
						tax: calculatedTotals.tax,
						shipping: calculatedTotals.shipping,
						total: calculatedTotals.total,
						total_discounts: calculatedTotals.totalDiscounts
					} : {
						subtotal: 0,
						tax: 0,
						shipping: 0,
						total: 0,
						total_discounts: 0
					}
				};

				const checkoutResponse = await checkoutService.processCheckout(checkoutRequestData, user?.email);
				
				console.log("Respuesta del checkout completo:", checkoutResponse);

				if (checkoutResponse.status === "success") {
					clearCart();
					setShowWidget(false);

					showNotification(
						NotificationType.SUCCESS,
						"¡Pago y pedido completados exitosamente!"
					);

					console.log("Detalles completos de la orden:", {
						datafast: verifyResponse.data,
						checkout: checkoutResponse.data
					});

					onSuccess?.(checkoutResponse.data);
					navigate("/orders");
				} else {
					throw new Error(checkoutResponse.message || "Error al procesar el checkout completo");
				}
			} else {
				throw new Error(verifyResponse.message || "Error en la simulación de Datafast");
			}
		} catch (error) {
			console.error("Error en la simulación completa:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Error al procesar la simulación completa";
			showNotification(NotificationType.ERROR, errorMessage);
			onError?.(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	const handleRealPayment = async () => {
		if (!checkoutResponse) {
			showNotification(NotificationType.ERROR, "No hay datos de checkout disponibles");
			return;
		}

		try {
			setIsLoading(true);
			
			const totals = cart ? await CheckoutItemsService.calculateCheckoutTotals(cart.items, appliedDiscount) : null;
			if (totals) {
				localStorage.setItem("datafast_calculated_total", totals.total.toString());
				console.log("💰 Total calculado guardado para verificación:", totals.total);
			}

			// ✅ GUARDAR DATOS DEL CARRITO PARA RECUPERACIÓN EN CASO DE ERROR
			console.log("💾 GUARDANDO BACKUP DEL CARRITO EN LOCALSTORAGE (ANTES DEL WIDGET)");
			console.log("   - Items en carrito:", cart?.items?.length || 0);
			console.log("   - Carrito completo:", cart);
			localStorage.setItem("datafast_cart_backup", JSON.stringify(cart));
			console.log("   ✅ Backup guardado exitosamente");
			
			// ✅ CONFIGURAR EL CALLBACK PARA MANEJAR EL RESULTADO DEL PAGO REAL
			const realPaymentSavedCard = isSavedCardFlow();
			const realPaymentOptions: Record<string, any> = {
				onReady: function() {
					console.log("🎯 Widget Datafast listo para pago real");
					const savedCardActive = isSavedCardFlow();
					ensureDatafastWidgetSelectors(
						installmentsRef.current,
						creditTypeRef.current,
						handleWidgetInstallmentsChange,
						handleWidgetCreditTypeChange
					);
					scheduleRestrictedFieldDedupe();
					insertCertificationBadge();
					setTimeout(insertCertificationBadge, 150);
					if (savedCardActive) {
						const hideSaveGroup = () => {
							const group = document.querySelector<HTMLElement>('.bcommerce-save-card-group');
							if (group) {
								group.style.display = "none";
							}
						};
						hideSaveGroup();
						setTimeout(hideSaveGroup, 100);
						showNotification(
							NotificationType.INFO,
							"Confirma el CVV de tu tarjeta guardada para continuar."
						);
					} else {
						ensureDatafastSaveCardOption(saveCardRef.current, handleWidgetSaveCardToggle);
						setTimeout(() => {
							ensureDatafastWidgetSelectors(
								installmentsRef.current,
								creditTypeRef.current,
								handleWidgetInstallmentsChange,
								handleWidgetCreditTypeChange
							);
							scheduleRestrictedFieldDedupe();
							ensureDatafastSaveCardOption(saveCardRef.current, handleWidgetSaveCardToggle);
						}, 100);
						showNotification(
							NotificationType.INFO,
							"Complete los datos de su tarjeta en el formulario y haga clic en 'Pagar'."
						);
					}
					clearInlineDatafastError();
				},
				onBeforeSubmitCard: function(data: any) {
					console.log("🔄 Validando datos antes del pago real...", data);

					if (isSavedCardFlow()) {
						dedupeRestrictedDatafastFields();
						scheduleRestrictedFieldDedupe();
						clearInlineDatafastError();
						return true;
					}

					const validation = validateDatafastCardFields(data);
					if (!validation.valid) {
						const firstMissing = validation.missingFields[0];
						const message = `Completa el ${firstMissing.label} para continuar`;
						console.warn("⚠️ Validación Datafast (pago real): faltan campos obligatorios:", validation.missingFields);
						showInlineDatafastError(message, firstMissing);
						focusFirstMissingDatafastField(validation.missingFields);
						setIsLoading(false);
						return false;
					}

					const currentPreference = readSaveCardPreferenceFromForm();
					persistSaveCardPreference(currentPreference);

					if (!ensureCardholderFilled()) {
						setIsLoading(false);
						return false;
					}

					clearInlineDatafastError();
					return true;
				},
				onSuccess: function(result: any) {
					console.log("🎉 Pago real exitoso:", result);
					window.location.href = `/datafast-result?resourcePath=${encodeURIComponent(result.resourcePath)}`;
				},
				onError: function(error: any) {
					console.error("❌ Error en pago real:", error);
					setIsLoading(false);
					showNotification(
						NotificationType.ERROR,
						"Error al procesar el pago. Inténtelo nuevamente."
					);
				}
			};

			if (realPaymentSavedCard) {
				realPaymentOptions.registrations = {
					requireCvv: true,
					hideInitialPaymentForms: true,
				};
			}

			(window as any).wpwlOptions = realPaymentOptions;

			console.log("🔄 Configuración lista para pago real con Datafast");
			
		} catch (error) {
			console.error("❌ Error configurando pago real:", error);
			setIsLoading(false);
			showNotification(
				NotificationType.ERROR,
				"Error al configurar el pago real"
			);
		}
	};

	// Determinar si estamos en desarrollo
	const isDevelopment = import.meta.env.MODE === 'development' || import.meta.env.VITE_APP_ENV === 'development';

	if (showWidget) {
		return (
			<div className="datafast-payment-widget">
				<div className="bg-white rounded-lg shadow-lg p-6">
					<h3 className="text-xl font-bold mb-4">Pagar con Datafast</h3>
					<div className="mb-4 p-4 bg-blue-50 rounded-lg">
				<h4 className="font-semibold text-blue-800">
					Información del pedido:
				</h4>
				<p className="text-blue-700">Monto: ${calculatedTotals?.total?.toFixed(2) || checkoutResponse?.amount || cart?.total}</p>
				<p className="text-blue-700">ID: {checkoutResponse?.transaction_id}</p>
				<p className="text-blue-700">
					Diferidos: {installments === 0 ? '0 (corriente)' : `${installments} meses`}
				</p>
				<p className="text-blue-700">Tipo de crédito: {selectedCreditTypeLabel}</p>
			</div>

					{savedCardsLoading ? (
						<div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
							Cargando tus tarjetas guardadas...
						</div>
					) : savedCards.length > 0 ? (
						<div className="mb-4 border border-primary-100 rounded-lg p-4 bg-white">
							<h4 className="text-sm font-semibold text-gray-900 mb-3">
								Selecciona una tarjeta guardada
							</h4>
							<div className="space-y-3">
								{savedCards.map((card) => {
									const isChecked = paymentMode === "saved" && selectedSavedCardId === card.id;
									return (
										<label
											key={card.id}
											className={`flex items-center justify-between px-3 py-2 border rounded-md cursor-pointer transition-colors ${
												isChecked ? "border-primary-500 bg-primary-50" : "border-gray-200 hover:border-primary-300"
											}`}
										>
											<div className="flex items-center space-x-3">
												<input
													type="radio"
													name="datafast-saved-card"
													checked={isChecked}
													onChange={() => handleSelectSavedCardOption(card.id)}
												/>
												<div>
													<p className="text-sm font-medium text-gray-900">
														{card.card_brand} terminada en {card.card_last_four}
														{card.is_default && (
															<span className="ml-2 inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
																Predeterminada
															</span>
														)}
													</p>
													<p className="text-xs text-gray-500">
														Titular: {card.card_holder_name}
														{card.expires_at ? ` · Expira ${new Date(card.expires_at).toLocaleDateString()}` : ""}
													</p>
												</div>
											</div>
										</label>
									);
								})}

								<label
									className={`flex items-center px-3 py-2 border rounded-md cursor-pointer transition-colors ${
										paymentMode === "new" ? "border-primary-500 bg-primary-50" : "border-gray-200 hover:border-primary-300"
									}`}
								>
									<input
										type="radio"
										name="datafast-saved-card"
										className="mr-3"
										checked={paymentMode === "new"}
										onChange={handleUseNewCardOption}
									/>
									<span className="text-sm text-gray-900">
										Usar una nueva tarjeta
									</span>
								</label>
							</div>
						</div>
					) : null}

					<div className="min-h-[400px] border border-gray-200 rounded-lg p-4">
						{/* El widget de Datafast inyectará su propio formulario aquí */}
						<form
							className="paymentWidgets"
							data-brands="VISA MASTER AMEX DINERS DISCOVER"
						>
							{!widgetLoaded && (
								<div className="flex items-center justify-center h-64">
									<div className="text-center">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
										<p className="text-gray-600">
											Cargando formulario de pago...
										</p>
									</div>
								</div>
							)}
						</form>
					</div>

					<div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
						<h4 className="font-semibold text-yellow-800 mb-2">
							Datos de prueba para usar:
						</h4>
						<div className="text-sm text-yellow-700 space-y-1">
							<p>
								<strong>Tarjeta VISA:</strong> 4200 0000 0000 0000
							</p>
							<p>
								<strong>Fecha:</strong> 07/26
							</p>
							<p>
								<strong>CVV:</strong> 246
							</p>
							<p>
								<strong>Titular:</strong> {formData.given_name}{" "}
								{formData.surname}
							</p>
							<p className="text-xs mt-2 text-blue-600">
								<strong>Fase 2:</strong> Credenciales de testing avanzado con transacciones reales limitadas
							</p>
						</div>
					</div>

					{isDevelopment && (
						<div className="mt-6 space-y-4">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<button
									onClick={handleRealPayment}
									disabled={!widgetLoaded}
									className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50"
								>
									Pago Real
								</button>

								<button
									onClick={handleSimulatePaymentResult}
									disabled={isLoading}
									className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50"
								>
									{isLoading ? "Verificando..." : "Simular Pago Exitoso"}
								</button>
							</div>

							<div className="border-t pt-4">
								<button
									onClick={handleCompleteTestCheckout}
									disabled={isLoading}
									className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-3 px-4 rounded-md transition-colors disabled:opacity-50 flex items-center justify-center"
								>
									{isLoading ? (
										<>
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
											Procesando...
										</>
									) : (
										<>
											<svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
											Prueba Completa de Checkout
										</>
									)}
								</button>
								<p className="text-xs text-gray-500 text-center mt-2">
									Simula el proceso completo de checkout como el botón de prueba original
								</p>
							</div>
						</div>
					)}

					<div className="flex justify-center">
						<button
							onClick={() => {
								setShowWidget(false);
								setShowForm(true);
								setWidgetLoaded(false);
								const script = document.getElementById("datafast-widget-script");
								if (script) script.remove();
							}}
							disabled={isLoading}
							className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
						>
							Volver
						</button>
					</div>
				</div>
			</div>
		);
	}

	if (showForm) {
		return (
			<div className="datafast-form">
				<div className="bg-white rounded-lg shadow-lg p-6">
					<h3 className="text-xl font-bold mb-4">Información para Datafast</h3>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Nombre *
							</label>
							<input
								type="text"
								value={formData.given_name}
								onChange={(e) =>
									handleInputChange("given_name", e.target.value)
								}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Segundo Nombre
							</label>
							<input
								type="text"
								value={formData.middle_name}
								onChange={(e) =>
									handleInputChange("middle_name", e.target.value)
								}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Apellido *
							</label>
							<input
								type="text"
								value={formData.surname}
								onChange={(e) => handleInputChange("surname", e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Teléfono *
							</label>
							<input
								type="text"
								value={formData.phone}
								onChange={(e) => handleInputChange("phone", e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
								placeholder="0999999999"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Cédula * (10 dígitos)
							</label>
							<input
								type="text"
								value={formData.doc_id}
								onChange={(e) => {
									// ✅ Permitir hasta 13 dígitos temporalmente para RUC, luego auto-extraer cédula
									const value = e.target.value.replace(/\D/g, "").slice(0, 13);
									handleInputChange("doc_id", value);
								}}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
								placeholder="1234567890 (si ingresa RUC se extraerá automáticamente la cédula)"
								maxLength={13}
							/>
						</div>

						<div className="md:col-span-2">
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Dirección *
							</label>
							<input
								type="text"
								value={formData.address}
								onChange={(e) => handleInputChange("address", e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Ciudad *
							</label>
							<input
								type="text"
								value={formData.city}
								onChange={(e) => handleInputChange("city", e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								País *
							</label>
							<select
								value={formData.country}
								onChange={(e) => handleInputChange("country", e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
							>
								<option value="EC">Ecuador</option>
								<option value="CO">Colombia</option>
								<option value="PE">Perú</option>
								<option value="US">Estados Unidos</option>
							</select>
						</div>

					</div>

					{savedCardsLoading ? (
						<div className="mt-4 text-sm text-gray-600">
							Cargando tarjetas guardadas...
						</div>
					) : savedCards.length > 0 ? (
						<div className="mt-6 border border-primary-100 rounded-lg p-4 bg-primary-50/30">
							<h4 className="text-sm font-semibold text-gray-900 mb-3">
								¿Quieres usar una tarjeta guardada?
							</h4>
							<div className="space-y-3">
								{savedCards.map((card) => {
									const isChecked = paymentMode === "saved" && selectedSavedCardId === card.id;
									return (
										<label
											key={card.id}
											className={`flex items-center justify-between px-3 py-2 border rounded-md cursor-pointer transition-colors ${
												isChecked ? "border-primary-500 bg-white" : "border-gray-200 hover:border-primary-300 bg-white"
											}`}
										>
											<div className="flex items-center space-x-3">
												<input
													type="radio"
													name="datafast-saved-card-form"
													checked={isChecked}
													onChange={() => handleSelectSavedCardOption(card.id)}
												/>
												<div>
													<p className="text-sm font-medium text-gray-900">
														{card.card_brand} terminada en {card.card_last_four}
														{card.is_default && (
															<span className="ml-2 inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
																Predeterminada
															</span>
														)}
													</p>
													<p className="text-xs text-gray-500">
														Titular: {card.card_holder_name}
													</p>
												</div>
											</div>
										</label>
									);
								})}

								<label
									className={`flex items-center px-3 py-2 border rounded-md cursor-pointer transition-colors bg-white ${
										paymentMode === "new" ? "border-primary-500" : "border-gray-200 hover:border-primary-300"
									}`}
								>
									<input
										type="radio"
										name="datafast-saved-card-form"
										className="mr-3"
										checked={paymentMode === "new"}
										onChange={handleUseNewCardOption}
									/>
									<span className="text-sm text-gray-900">
										Ingresar una nueva tarjeta
									</span>
								</label>
							</div>
						</div>
					) : null}

					<div className="mt-6 flex gap-4">
						<button
							onClick={() => handleStartPayment()}
							disabled={isLoading}
							className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50 flex items-center justify-center"
						>
							{isLoading ? (
								<>
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
									Creando checkout...
								</>
							) : (
								"Continuar con Datafast"
							)}
						</button>

						<button
							onClick={() => setShowForm(false)}
							disabled={isLoading}
							className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
						>
							Cancelar
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="datafast-payment-button">
			<button
				onClick={() => setShowForm(true)}
				disabled={isLoading || !cart || cart.items.length === 0}
				className="w-full transition-all duration-200 transform hover:scale-101 bg-[#003c58] border-1 hover:bg-[#00B86E] text-white font-medium py-3 px-4 rounded-md disabled:opacity-50 flex items-center justify-center"
			>
				<svg
					className="w-5 h-5 mr-2"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
					/>
				</svg>
				Pagar con Datafast
			</button>

			{(!cart || cart.items.length === 0) && (
				<p className="mt-2 text-sm text-gray-500 text-center">
					Agrega productos al carrito para continuar
				</p>
			)}
		</div>
	);
};

export default DatafastPaymentButton;
